/**
 * Critic Performance Dashboard — observability for the Bitsy ReAct safety critic.
 *
 * Reads /api/admin/critic-stats and renders:
 *  - Counts (approved / blocked / executed / premium) for the selected window
 *  - Pass 1 + Pass 2 latency stats (avg / p95 / p2-runs)
 *  - Top 5 block reasons (helps tune the prompt)
 *  - Fail-OPEN events (prompt drift / API outage indicator)
 *  - Recent activity feed
 */
import { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, ShieldCheck, ShieldAlert, Clock, AlertTriangle, RefreshCw, Plus, X, Check } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { API } from '@/lib/backendUrl';

const WINDOWS = [
  { value: 1, label: '24h' },
  { value: 7, label: '7d' },
  { value: 30, label: '30d' },
];

export default function CriticStatsPage() {
  const nav = useNavigate();
  const [days, setDays] = useState(7);
  const [hotelId, setHotelId] = useState('');
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [overrides, setOverrides] = useState([]);
  const [showAddModal, setShowAddModal] = useState(null); // { kind, value, suggestedReason }
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' | 'audits'
  const [audits, setAudits] = useState([]);
  const [openAudit, setOpenAudit] = useState(null); // full doc with markdown
  const [runningAudit, setRunningAudit] = useState(false);
  const [emailCfg, setEmailCfg] = useState(null);
  const [emailDraft, setEmailDraft] = useState({ enabled: false, recipient_email: '' });
  const [emailSaving, setEmailSaving] = useState(false);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [emailToast, setEmailToast] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (hotelId.trim()) params.set('hotel_id', hotelId.trim());
      const res = await fetch(`${API}/api/admin/critic-stats?${params}`);
      const j = await res.json();
      setData(j);
    } catch {
      setData(null);
    }
    setLoading(false);
  };

  const loadOverrides = useCallback(async () => {
    try {
      const params = new URLSearchParams();
      if (hotelId.trim()) params.set('hotel_id', hotelId.trim());
      const res = await fetch(`${API}/api/admin/critic-stats/overrides?${params}`);
      const j = await res.json();
      setOverrides(j.overrides || []);
    } catch {
      setOverrides([]);
    }
  }, [hotelId]);

  const submitOverride = async (kind, value, reason) => {
    const body = {
      kind,
      value,
      reason,
      added_by: 'admin@hermes-protocol.com', // TODO: pull from auth context
    };
    if (hotelId.trim()) body.hotel_id = hotelId.trim();
    const res = await fetch(`${API}/api/admin/critic-stats/overrides`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(body),
    });
    if (res.ok) {
      setShowAddModal(null);
      await loadOverrides();
    }
  };

  const deleteOverride = async (id) => {
    const params = new URLSearchParams({ disabled_by: 'admin@hermes-protocol.com' });
    await fetch(`${API}/api/admin/critic-stats/overrides/${id}?${params}`, { method: 'DELETE' });
    await loadOverrides();
  };

  const loadAudits = useCallback(async () => {
    try {
      const params = new URLSearchParams({ limit: '12' });
      if (hotelId.trim()) params.set('hotel_id', hotelId.trim());
      const res = await fetch(`${API}/api/admin/critic-stats/self-audits?${params}`);
      const j = await res.json();
      setAudits(j.audits || []);
    } catch {
      setAudits([]);
    }
  }, [hotelId]);

  const openAuditDetails = async (id) => {
    try {
      const res = await fetch(`${API}/api/admin/critic-stats/self-audits/${id}`);
      const j = await res.json();
      setOpenAudit(j);
    } catch { /* ignore */ }
  };

  const runAuditNow = async () => {
    setRunningAudit(true);
    try {
      const params = new URLSearchParams({ days: String(days) });
      if (hotelId.trim()) params.set('hotel_id', hotelId.trim());
      const res = await fetch(`${API}/api/admin/critic-stats/self-audits/run-now?${params}`, { method: 'POST' });
      const j = await res.json();
      if (j.id) {
        setOpenAudit(j);
        await loadAudits();
      }
    } catch { /* ignore */ }
    setRunningAudit(false);
  };

  const loadEmailCfg = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/admin/critic-stats/email-config`);
      const j = await res.json();
      setEmailCfg(j);
      setEmailDraft({ enabled: !!j.enabled, recipient_email: j.recipient_email || '' });
    } catch { setEmailCfg(null); }
  }, []);

  const saveEmailCfg = async () => {
    setEmailSaving(true);
    try {
      const res = await fetch(`${API}/api/admin/critic-stats/email-config`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(emailDraft),
      });
      if (res.ok) {
        const j = await res.json();
        setEmailCfg(j);
        setEmailToast({ ok: true, msg: 'Saved' });
      } else {
        const err = await res.json().catch(() => ({}));
        setEmailToast({ ok: false, msg: err.detail || 'Save failed' });
      }
    } catch { setEmailToast({ ok: false, msg: 'Save failed' }); }
    setEmailSaving(false);
    setTimeout(() => setEmailToast(null), 2500);
  };

  const sendAuditEmail = async (auditId) => {
    setSendingEmailId(auditId);
    try {
      const res = await fetch(`${API}/api/admin/critic-stats/self-audits/${auditId}/send-email`, { method: 'POST' });
      const j = await res.json();
      setEmailToast({ ok: j.status === 'sent', msg: j.status === 'sent' ? `Sent · ${j.subject?.slice(0, 60) || ''}` : `Skipped: ${j.reason || j.error || 'unknown'}` });
    } catch { setEmailToast({ ok: false, msg: 'Send failed' }); }
    setSendingEmailId(null);
    setTimeout(() => setEmailToast(null), 4000);
  };

  // Heuristic: turn a block reason into a suggested whitelist override
  const suggestOverrideFromReason = (reason) => {
    const lc = reason.toLowerCase();
    // Email domain pattern
    const emailDomain = reason.match(/@[a-z0-9.-]+/i);
    if (emailDomain && (lc.includes('email') || lc.includes('domain'))) {
      return { kind: 'email_domain', value: emailDomain[0], suggestedReason: `Whitelisted via dashboard — ${reason.slice(0, 100)}` };
    }
    // Room ID substring
    const roomMatch = reason.match(/room_id ['"]([^'"]+)['"]/i) || reason.match(/contains ['"]([^'"]+)['"]/i);
    if (roomMatch && lc.includes('room')) {
      return { kind: 'room_id_substring', value: roomMatch[1], suggestedReason: `Whitelisted via dashboard — ${reason.slice(0, 100)}` };
    }
    // Phone fallback
    if (lc.includes('phone')) {
      return { kind: 'phone_pattern', value: '+1-XXX-XXX-XXXX', suggestedReason: `Whitelisted via dashboard — ${reason.slice(0, 100)}` };
    }
    return { kind: 'custom_rule', value: '', suggestedReason: `Reverse rule for: ${reason.slice(0, 100)}` };
  };

  useEffect(() => {
    load();
    loadOverrides();
    loadAudits();
    loadEmailCfg();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [days]);

  const blockRatePct = data ? (data.block_rate * 100).toFixed(1) : '0.0';

  return (
    <div className="min-h-screen bg-zinc-950 text-zinc-100" data-testid="critic-stats-page">
      <div className="border-b border-zinc-800/60 bg-zinc-900/40 backdrop-blur">
        <div className="max-w-6xl mx-auto px-6 py-4 flex items-center gap-4">
          <Button variant="ghost" size="sm" onClick={() => nav('/admin')} data-testid="back-to-admin-btn">
            <ArrowLeft className="w-4 h-4 mr-1" /> Admin
          </Button>
          <div className="flex-1">
            <h1 className="text-lg font-semibold tracking-tight">Critic Performance</h1>
            <p className="text-xs text-zinc-500">Bitsy ReAct safety critic — verdicts, latency, prompt-drift signals.</p>
          </div>
          <Button size="sm" variant="outline" onClick={load} disabled={loading} data-testid="refresh-btn">
            <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          </Button>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-6 py-6 space-y-5">
        {/* Filter row */}
        <div className="flex flex-wrap items-center gap-3" data-testid="filter-bar">
          <div className="flex items-center gap-1 border border-zinc-800 rounded-lg p-1 bg-zinc-900/50">
            {WINDOWS.map(w => (
              <button
                key={w.value}
                onClick={() => setDays(w.value)}
                className={`px-3 py-1 rounded-md text-xs font-medium transition ${days === w.value ? 'bg-amber-500 text-black' : 'text-zinc-400 hover:text-zinc-100'}`}
                data-testid={`window-${w.label}`}
              >
                {w.label}
              </button>
            ))}
          </div>
          <input
            type="text"
            value={hotelId}
            onChange={(e) => setHotelId(e.target.value)}
            onKeyDown={(e) => { if (e.key === 'Enter') load(); }}
            placeholder="filter hotel_id (optional)"
            className="text-xs bg-zinc-900/50 border border-zinc-800 rounded-md px-3 py-1.5 placeholder-zinc-600 focus:outline-none focus:border-amber-500/40 w-56"
            data-testid="hotel-filter-input"
          />
          <Button size="sm" variant="outline" onClick={load} data-testid="apply-filter-btn">Apply</Button>
        </div>

        {/* Tabs */}
        <div className="flex border-b border-zinc-800/60 -mt-2" data-testid="tabs-bar">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${activeTab === 'overview' ? 'border-amber-500 text-zinc-100' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            data-testid="tab-overview"
          >
            Overview
          </button>
          <button
            onClick={() => setActiveTab('audits')}
            className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${activeTab === 'audits' ? 'border-amber-500 text-zinc-100' : 'border-transparent text-zinc-500 hover:text-zinc-300'}`}
            data-testid="tab-audits"
          >
            Self-Audits
          </button>
        </div>

        {activeTab === 'overview' && (!data || data.total_checks === 0) ? (
          <Card className="bg-zinc-900 border-zinc-800">
            <CardContent className="p-8 text-center text-zinc-500" data-testid="empty-state">
              {loading ? 'Loading…' : 'No critic checks recorded in this window. Make a /chat-react booking attempt to populate.'}
            </CardContent>
          </Card>
        ) : activeTab === 'overview' ? (
          <>
            {/* KPI cards */}
            <div className="grid grid-cols-2 md:grid-cols-5 gap-3" data-testid="kpi-grid">
              <Kpi label="Total checks" value={data.total_checks} testid="kpi-total" />
              <Kpi label="Approved" value={data.approved} tone="emerald" icon={ShieldCheck} testid="kpi-approved" />
              <Kpi label="Blocked" value={data.blocked} sub={`${blockRatePct}% block rate`} tone="red" icon={ShieldAlert} testid="kpi-blocked" />
              <Kpi label="Premium tier" value={data.premium_count} sub="dual-pass" tone="amber" testid="kpi-premium" />
              <Kpi
                label="Fail-OPEN"
                value={data.fail_open_events}
                sub={data.fail_open_events > 0 ? 'CHECK OUTAGE' : 'healthy'}
                tone={data.fail_open_events > 0 ? 'red' : 'zinc'}
                icon={AlertTriangle}
                testid="kpi-fail-open"
              />
            </div>

            {/* Latency */}
            <Card className="bg-zinc-900 border-zinc-800" data-testid="latency-card">
              <CardContent className="p-5">
                <div className="flex items-center gap-2 mb-3">
                  <Clock className="w-4 h-4 text-amber-400" />
                  <p className="text-sm font-semibold text-zinc-100">Latency</p>
                </div>
                <div className="grid md:grid-cols-2 gap-4">
                  <LatencyBlock label="Pass 1 (every check)" stat={data.pass_1} testid="lat-pass-1" />
                  <LatencyBlock label="Pass 2 (premium only)" stat={data.pass_2} extra={`${data.pass_2.runs || 0} runs`} testid="lat-pass-2" />
                </div>
              </CardContent>
            </Card>

            {/* Block reasons */}
            <Card className="bg-zinc-900 border-zinc-800" data-testid="block-reasons-card">
              <CardContent className="p-5">
                <p className="text-sm font-semibold text-zinc-100 mb-3">Top block reasons</p>
                {data.top_block_reasons.length === 0 ? (
                  <p className="text-xs text-zinc-500">No blocks in this window. Either nothing fishy hit the critic, or volume was low.</p>
                ) : (
                  <ul className="space-y-2">
                    {data.top_block_reasons.map((r, i) => (
                      <li key={i} className="flex items-start gap-3 text-xs group" data-testid={`block-reason-${i}`}>
                        <span className="w-7 text-right text-zinc-500 shrink-0">{r.count}×</span>
                        <span className="flex-1 text-zinc-300 leading-snug">{r.reason}</span>
                        <button
                          onClick={() => setShowAddModal(suggestOverrideFromReason(r.reason))}
                          className="opacity-0 group-hover:opacity-100 transition text-[10px] px-2 py-0.5 rounded bg-amber-500/15 text-amber-300 hover:bg-amber-500/25 border border-amber-500/30 shrink-0"
                          data-testid={`whitelist-btn-${i}`}
                          title="Add an exception so the critic stops blocking on this pattern"
                        >
                          Whitelist
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Active prompt overrides */}
            <Card className="bg-zinc-900 border-zinc-800" data-testid="overrides-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-zinc-100">Active prompt overrides</p>
                    <p className="text-[11px] text-zinc-500">Whitelist exceptions injected into the critic prompt at runtime.</p>
                  </div>
                  <Button
                    size="sm"
                    variant="outline"
                    onClick={() => setShowAddModal({ kind: 'email_domain', value: '', suggestedReason: '' })}
                    data-testid="add-override-btn"
                  >
                    <Plus className="w-3.5 h-3.5 mr-1" /> Add
                  </Button>
                </div>
                {overrides.length === 0 ? (
                  <p className="text-xs text-zinc-500" data-testid="overrides-empty">No active overrides — the critic is running on the static prompt only.</p>
                ) : (
                  <ul className="space-y-1.5" data-testid="overrides-list">
                    {overrides.map((o) => (
                      <li
                        key={o.id}
                        className="flex items-start gap-3 text-xs bg-zinc-950/40 border border-zinc-800/60 rounded-md px-3 py-2"
                        data-testid={`override-${o.id}`}
                      >
                        <Badge variant="outline" className="text-[10px] shrink-0 border-amber-500/30 text-amber-300">{o.kind}</Badge>
                        <div className="flex-1 min-w-0">
                          <p className="font-mono text-zinc-200 truncate">{o.value}</p>
                          <p className="text-[10px] text-zinc-500 mt-0.5 leading-snug">
                            {o.reason} · added by <span className="text-zinc-400">{o.added_by}</span>
                            {o.hotel_id ? <> · <span className="text-amber-400">{o.hotel_id}</span></> : <> · <span className="text-zinc-500">global</span></>}
                          </p>
                        </div>
                        <button
                          onClick={() => deleteOverride(o.id)}
                          className="text-zinc-500 hover:text-red-400 transition shrink-0"
                          data-testid={`delete-override-${o.id}`}
                          title="Disable this override"
                        >
                          <X className="w-3.5 h-3.5" />
                        </button>
                      </li>
                    ))}
                  </ul>
                )}
              </CardContent>
            </Card>

            {/* Recent activity */}
            <Card className="bg-zinc-900 border-zinc-800" data-testid="recent-card">
              <CardContent className="p-5">
                <p className="text-sm font-semibold text-zinc-100 mb-3">Recent checks</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-xs">
                    <thead>
                      <tr className="text-zinc-500 border-b border-zinc-800">
                        <th className="text-left py-2 px-2 font-medium">Time (UTC)</th>
                        <th className="text-left py-2 px-2 font-medium">Hotel</th>
                        <th className="text-left py-2 px-2 font-medium">Tool</th>
                        <th className="text-left py-2 px-2 font-medium">Tier</th>
                        <th className="text-left py-2 px-2 font-medium">Pass 1</th>
                        <th className="text-left py-2 px-2 font-medium">Pass 2</th>
                        <th className="text-left py-2 px-2 font-medium">Final</th>
                      </tr>
                    </thead>
                    <tbody data-testid="recent-table-body">
                      {data.recent.map((r, i) => (
                        <tr key={i} className="border-b border-zinc-800/60 hover:bg-zinc-800/30">
                          <td className="py-2 px-2 text-zinc-500 font-mono">{(r.ts || '').slice(11, 19)}</td>
                          <td className="py-2 px-2 text-zinc-400 truncate max-w-[160px]">{r.hotel_id || '—'}</td>
                          <td className="py-2 px-2 text-zinc-300">{r.tool_name}</td>
                          <td className="py-2 px-2">
                            <span className={`px-1.5 py-0.5 rounded text-[10px] ${r.tier === 'premium' ? 'bg-amber-500/15 text-amber-300' : 'bg-zinc-800 text-zinc-400'}`}>
                              {r.tier}
                            </span>
                          </td>
                          <td className="py-2 px-2"><Verdict v={r.p1_verdict} ms={r.p1_latency_ms} /></td>
                          <td className="py-2 px-2">{r.p2_verdict ? <Verdict v={r.p2_verdict} ms={r.p2_latency_ms} /> : <span className="text-zinc-600">—</span>}</td>
                          <td className="py-2 px-2"><Verdict v={r.final_verdict} bold /></td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </CardContent>
            </Card>
          </>
        ) : null}

        {activeTab === 'audits' && (
          <div className="space-y-4" data-testid="audits-panel">
            <div className="flex items-center justify-between">
              <p className="text-xs text-zinc-500">
                AI-generated weekly review of critic behaviour. Cron: <span className="font-mono text-zinc-400">Mondays 11:00 UTC</span>.
              </p>
              <Button
                size="sm"
                onClick={runAuditNow}
                disabled={runningAudit}
                className="bg-amber-500 hover:bg-amber-500/90 text-black"
                data-testid="run-audit-now-btn"
              >
                {runningAudit ? 'Generating…' : 'Generate now'}
              </Button>
            </div>

            {/* Email delivery config */}
            <Card className="bg-zinc-900 border-zinc-800" data-testid="email-config-card">
              <CardContent className="p-5">
                <div className="flex items-center justify-between mb-3">
                  <div>
                    <p className="text-sm font-semibold text-zinc-100">Weekly email delivery</p>
                    <p className="text-[11px] text-zinc-500">
                      Auto-emails the global audit every Monday 11:05 UTC.
                      {emailCfg?.brevo_configured === false && <span className="ml-1 text-red-400">⚠ BREVO_API_KEY not set in backend env</span>}
                    </p>
                  </div>
                  <label className="flex items-center gap-2 cursor-pointer text-xs text-zinc-300">
                    <input
                      type="checkbox"
                      checked={emailDraft.enabled}
                      onChange={(e) => setEmailDraft({ ...emailDraft, enabled: e.target.checked })}
                      className="accent-amber-500"
                      data-testid="email-enabled-toggle"
                    />
                    Enabled
                  </label>
                </div>
                <div className="flex flex-wrap gap-2 items-end">
                  <div className="flex-1 min-w-[260px]">
                    <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Recipient</label>
                    <input
                      type="email"
                      value={emailDraft.recipient_email}
                      onChange={(e) => setEmailDraft({ ...emailDraft, recipient_email: e.target.value })}
                      placeholder="ops@yourcompany.com"
                      className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-1.5 text-sm text-zinc-100 focus:outline-none focus:border-amber-500/40 font-mono"
                      data-testid="email-recipient-input"
                    />
                  </div>
                  <Button
                    size="sm"
                    onClick={saveEmailCfg}
                    disabled={emailSaving}
                    className="bg-amber-500 hover:bg-amber-500/90 text-black"
                    data-testid="email-save-btn"
                  >
                    {emailSaving ? 'Saving…' : 'Save'}
                  </Button>
                </div>
                {emailCfg?.updated_at && (
                  <p className="text-[10px] text-zinc-500 mt-2 font-mono">Last updated: {(emailCfg.updated_at || '').slice(0, 19).replace('T', ' ')} UTC</p>
                )}
              </CardContent>
            </Card>

            {audits.length === 0 ? (
              <Card className="bg-zinc-900 border-zinc-800">
                <CardContent className="p-8 text-center text-zinc-500" data-testid="audits-empty">
                  No self-audits yet. Click <span className="text-amber-300">Generate now</span> to produce one for the current scope.
                </CardContent>
              </Card>
            ) : (
              <div className="space-y-2" data-testid="audits-list">
                {audits.map((a) => (
                  <div
                    key={a.id}
                    className="bg-zinc-900 border border-zinc-800 rounded-lg p-4 hover:border-amber-500/30 transition group"
                    data-testid={`audit-row-${a.id}`}
                  >
                    <div className="flex items-start justify-between gap-3">
                      <button
                        onClick={() => openAuditDetails(a.id)}
                        className="text-left min-w-0 flex-1"
                        data-testid={`audit-open-${a.id}`}
                      >
                        <p className="text-sm font-semibold text-zinc-100 truncate">
                          {a.scope === 'global' ? '🌐 Global' : `🏨 ${a.scope}`} · {a.window_days}d window
                        </p>
                        <p className="text-[11px] text-zinc-500 mt-0.5 font-mono">
                          {(a.generated_at || '').replace('T', ' ').slice(0, 19)} UTC
                          {a.used_llm ? <span className="ml-2 text-emerald-400">· AI-generated</span> : <span className="ml-2 text-zinc-600">· heuristic fallback</span>}
                        </p>
                      </button>
                      <div className="flex flex-wrap gap-1.5 shrink-0 items-center">
                        <Badge variant="outline" className="text-[10px] border-zinc-700 text-zinc-300">{a.stats_summary?.total_checks ?? 0} checks</Badge>
                        {(a.stats_summary?.blocked ?? 0) > 0 && <Badge variant="outline" className="text-[10px] border-red-500/30 text-red-300">{a.stats_summary.blocked} blocked</Badge>}
                        {(a.stats_summary?.fail_open_events ?? 0) > 0 && <Badge variant="outline" className="text-[10px] border-red-500/40 text-red-400">⚠ {a.stats_summary.fail_open_events} fail-open</Badge>}
                        {(a.stats_summary?.stale_overrides ?? 0) > 0 && <Badge variant="outline" className="text-[10px] border-amber-500/30 text-amber-300">{a.stats_summary.stale_overrides} stale</Badge>}
                        <button
                          onClick={(e) => { e.stopPropagation(); sendAuditEmail(a.id); }}
                          disabled={sendingEmailId === a.id}
                          className="text-[10px] px-2 py-1 rounded bg-zinc-800 text-zinc-300 hover:bg-zinc-700 hover:text-white disabled:opacity-50 transition"
                          data-testid={`audit-send-email-${a.id}`}
                          title={emailCfg?.enabled ? `Email to ${emailCfg.recipient_email}` : 'Enable email delivery first'}
                        >
                          {sendingEmailId === a.id ? '…' : '✉ Send'}
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Self-Audit drawer */}
      {openAudit && (
        <div
          className="fixed inset-0 bg-black/70 backdrop-blur-sm flex justify-end z-40"
          onClick={() => setOpenAudit(null)}
          data-testid="audit-drawer-overlay"
        >
          <div
            className="bg-zinc-950 border-l border-zinc-800 w-full max-w-2xl h-full overflow-y-auto"
            onClick={(e) => e.stopPropagation()}
            data-testid="audit-drawer"
          >
            <div className="sticky top-0 bg-zinc-950 border-b border-zinc-800 px-6 py-3 flex items-center justify-between">
              <p className="text-sm text-zinc-400">
                {openAudit.scope === 'global' ? '🌐 Global' : `🏨 ${openAudit.scope}`} · {(openAudit.generated_at || '').slice(0, 19).replace('T', ' ')} UTC
              </p>
              <button onClick={() => setOpenAudit(null)} className="text-zinc-500 hover:text-zinc-100" data-testid="audit-drawer-close">
                <X className="w-4 h-4" />
              </button>
            </div>
            <div className="px-6 py-5 prose prose-invert prose-sm max-w-none" data-testid="audit-markdown">
              <pre className="whitespace-pre-wrap font-sans text-[13px] leading-relaxed text-zinc-200">{openAudit.markdown}</pre>
            </div>
          </div>
        </div>
      )}

      {/* Email status toast */}
      {emailToast && (
        <div
          className={`fixed bottom-6 right-6 z-50 rounded-lg px-4 py-3 text-xs font-medium shadow-lg border ${emailToast.ok ? 'bg-emerald-500/15 border-emerald-500/40 text-emerald-200' : 'bg-red-500/15 border-red-500/40 text-red-200'}`}
          data-testid="email-toast"
        >
          {emailToast.msg}
        </div>
      )}

      {/* Add Override Modal */}
      {showAddModal && (
        <AddOverrideModal
          initial={showAddModal}
          onCancel={() => setShowAddModal(null)}
          onSubmit={submitOverride}
        />
      )}
    </div>
  );
}

function AddOverrideModal({ initial, onCancel, onSubmit }) {
  const [kind, setKind] = useState(initial.kind || 'email_domain');
  const [value, setValue] = useState(initial.value || '');
  const [reason, setReason] = useState(initial.suggestedReason || '');
  const [submitting, setSubmitting] = useState(false);

  const handleSubmit = async () => {
    if (!value.trim() || !reason.trim()) return;
    setSubmitting(true);
    try {
      await onSubmit(kind, value.trim(), reason.trim());
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div
      className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-50 p-4"
      onClick={onCancel}
      data-testid="override-modal"
    >
      <div
        className="bg-zinc-900 border border-zinc-800 rounded-xl p-6 max-w-lg w-full"
        onClick={(e) => e.stopPropagation()}
      >
        <h3 className="text-lg font-semibold text-zinc-100 mb-1">Add critic exception</h3>
        <p className="text-xs text-zinc-500 mb-5">This appends a rule to the critic prompt so it stops false-positive blocking on the pattern below.</p>

        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Kind</label>
        <select
          value={kind}
          onChange={(e) => setKind(e.target.value)}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 mb-4 focus:outline-none focus:border-amber-500/40"
          data-testid="modal-kind-select"
        >
          <option value="email_domain">Email domain (e.g. @corp.example.com)</option>
          <option value="phone_pattern">Phone pattern (e.g. +1-415-555-XXXX)</option>
          <option value="room_id_substring">Room ID substring (e.g. premium_test)</option>
          <option value="custom_rule">Custom rule (free-text)</option>
        </select>

        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Value</label>
        <input
          type="text"
          value={value}
          onChange={(e) => setValue(e.target.value)}
          placeholder={
            kind === 'email_domain' ? '@corp.example.com'
            : kind === 'phone_pattern' ? '+1-415-555-XXXX'
            : kind === 'room_id_substring' ? 'premium_test'
            : 'Free-text rule the critic should follow'
          }
          className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-100 font-mono mb-4 focus:outline-none focus:border-amber-500/40"
          data-testid="modal-value-input"
          autoFocus
        />

        <label className="block text-[11px] uppercase tracking-wider text-zinc-500 mb-1">Reason (audit trail)</label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="Why are you whitelisting this? (visible to other admins later)"
          rows={3}
          className="w-full bg-zinc-950 border border-zinc-800 rounded-md px-3 py-2 text-sm text-zinc-200 mb-5 focus:outline-none focus:border-amber-500/40"
          data-testid="modal-reason-input"
        />

        <div className="flex justify-end gap-2">
          <Button variant="ghost" size="sm" onClick={onCancel} data-testid="modal-cancel-btn">Cancel</Button>
          <Button
            size="sm"
            className="bg-amber-500 hover:bg-amber-500/90 text-black"
            onClick={handleSubmit}
            disabled={submitting || !value.trim() || !reason.trim()}
            data-testid="modal-submit-btn"
          >
            <Check className="w-3.5 h-3.5 mr-1" /> {submitting ? 'Adding…' : 'Add exception'}
          </Button>
        </div>
      </div>
    </div>
  );
}

function Kpi({ label, value, sub, tone = 'zinc', icon: Icon, testid }) {
  const toneMap = {
    zinc: 'text-zinc-100',
    emerald: 'text-emerald-300',
    red: 'text-red-300',
    amber: 'text-amber-300',
  };
  return (
    <Card className="bg-zinc-900 border-zinc-800" data-testid={testid}>
      <CardContent className="p-4">
        <div className="flex items-start justify-between">
          <p className="text-[11px] uppercase tracking-wider text-zinc-500">{label}</p>
          {Icon ? <Icon className={`w-3.5 h-3.5 ${toneMap[tone]}`} /> : null}
        </div>
        <p className={`text-2xl font-semibold mt-1 ${toneMap[tone]}`}>{value}</p>
        {sub ? <p className="text-[10px] text-zinc-500 mt-0.5">{sub}</p> : null}
      </CardContent>
    </Card>
  );
}

function LatencyBlock({ label, stat, extra, testid }) {
  return (
    <div data-testid={testid}>
      <p className="text-[11px] uppercase tracking-wider text-zinc-500 mb-1.5">{label} {extra ? <span className="text-zinc-600 normal-case ml-1">· {extra}</span> : null}</p>
      <div className="flex items-baseline gap-4">
        <div>
          <p className="text-xl font-semibold text-zinc-100">{stat.avg_latency_ms}<span className="text-xs text-zinc-500 ml-0.5">ms</span></p>
          <p className="text-[10px] text-zinc-500">avg</p>
        </div>
        <div>
          <p className="text-xl font-semibold text-amber-300">{stat.p95_latency_ms}<span className="text-xs text-zinc-500 ml-0.5">ms</span></p>
          <p className="text-[10px] text-zinc-500">p95</p>
        </div>
      </div>
    </div>
  );
}

function Verdict({ v, ms, bold = false }) {
  if (!v) return <span className="text-zinc-600">—</span>;
  const cls = v === 'approve'
    ? 'text-emerald-300'
    : v === 'block'
    ? 'text-red-300'
    : 'text-zinc-400';
  return (
    <span className={`${cls} ${bold ? 'font-semibold' : ''}`}>
      {v}
      {ms ? <span className="text-zinc-600 ml-1">· {ms}ms</span> : null}
    </span>
  );
}
